import { usePanelTheme } from "../context/themeContext";
import React, { useMemo, useState, useEffect } from "react";
import { loginUser } from "../services/authService";
import { getCachedCompanyBranding } from "../services/companySettingsService";
import alFlag from "../assets/al.svg";
import deFlag from "../assets/de.svg";
import { motion, AnimatePresence } from "framer-motion";

export default function Login({ t, lang, setLang }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [company] = useState(() => getCachedCompanyBranding());
  const [showPassword, setShowPassword] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  const { currentTheme } = usePanelTheme();
  const isGerman = lang === "de";

  // Auto-rotate features on the left panel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      title: isGerman ? "Finanzen in Echtzeit" : "Financa në kohë reale",
      description: isGerman
        ? "Verfolge Einnahmen und Ausgaben in Echtzeit"
        : "Gjurmo të ardhurat dhe shpenzimet në kohë reale",
      icon: "💰",
    },
    {
      title: isGerman ? "Material- & Lagerkontrolle" : "Kontroll i materialeve dhe stokut",
      description: isGerman
        ? "Verwalte Materialien und Lagerbestände"
        : "Menaxho materialet dhe stokun",
      icon: "📦",
    },
    {
      title: isGerman ? "Mitarbeiter- & Zahlungsverfolgung" : "Gjurmim i punëtorëve dhe pagesave",
      description: isGerman
        ? "Verfolge Arbeitszeiten und Zahlungen"
        : "Gjurmo oraret dhe pagesat",
      icon: "👥",
    },
    {
      title: isGerman ? "Sicherer Zugriff" : "Qasje e sigurt",
      description: isGerman
        ? "Deine Daten werden zentral und geschützt verwaltet"
        : "Të dhënat menaxhohen në mënyrë qendrore dhe të mbrojtur",
      icon: "🔒",
    },
  ];

  const labels = useMemo(
    () => ({
      welcomeTitle:
        t.loginWelcomeTitle ||
        (isGerman ? "Willkommen bei Bauheld" : "Mirë se erdhe në Bauheld"),
      welcomeSubtitle:
        t.loginWelcomeSubtitle ||
        (isGerman
          ? "Verwalte Finanzen, Materialien, Mitarbeiter und Zahlungen in einem modernen System."
          : "Menaxho financat, materialet, punëtorët dhe pagesat në një sistem modern."),
      loginSubtitle:
        t.loginSubtitle ||
        (isGerman
          ? "Melde dich an, um dein Unternehmen zu verwalten."
          : "Kyçu për të menaxhuar kompaninë tënde."),
      email: t.emailLabel || "Email",
      password:
        t.passwordLabel || (isGerman ? "Passwort" : "Fjalëkalimi"),
      emailPlaceholder:
        t.emailPlaceholder ||
        (isGerman ? "name@firma.de" : "emri@firma.com"),
      passwordPlaceholder:
        t.passwordPlaceholder ||
        (isGerman ? "Passwort eingeben" : "Shkruaj fjalëkalimin"),
      loginButton: t.loginButton || (isGerman ? "Anmelden" : "Hyr"),
      loading:
        t.loading || (isGerman ? "Wird geladen..." : "Duke ngarkuar..."),
      loginError:
        t.loginError ||
        (isGerman
          ? "Anmeldung fehlgeschlagen. Prüfe E-Mail und Passwort."
          : "Hyrja dështoi. Kontrollo emailin dhe fjalëkalimin."),
      privateApp:
        t.privateAppLabel ||
        (isGerman ? "Privates Firmensystem" : "Sistem privat i kompanisë"),
      languageLabel:
        t.language || (isGerman ? "Sprache" : "Gjuha"),
    }),
    [t, isGerman]
  );

  const companyName = company.companyName || t.appName || "Bauheld";

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      await loginUser(email, password);
    } catch (error) {
      console.error("Auth error:", error);
      alert(labels.loginError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${currentTheme.pageBg} relative overflow-hidden`}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500/5 blur-3xl" />
      </div>

      <div className="relative grid min-h-screen grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left Panel - Hero Section */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="relative hidden overflow-hidden lg:flex"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a2f] via-[#0d1f36] to-[#0b1a2c]" />
          
          {/* Animated gradient orbs */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 h-96 w-96 animate-pulse rounded-full bg-blue-600/20 blur-3xl" />
            <div className="absolute bottom-0 right-0 h-96 w-96 animate-pulse rounded-full bg-indigo-600/20 blur-3xl" />
          </div>

          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="h-full w-full" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="relative z-10 flex w-full flex-col justify-between p-12 text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs uppercase tracking-wider text-white/80">
                  {labels.privateApp}
                </span>
              </div>
              
              <h1 className="mt-8 max-w-xl text-5xl font-black leading-tight">
                {labels.welcomeTitle.split(' ').map((word, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="inline-block mr-2"
                  >
                    {word}
                  </motion.span>
                ))}
              </h1>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-4 max-w-lg text-base text-white/70"
              >
                {labels.welcomeSubtitle}
              </motion.p>
            </motion.div>

            {/* Animated Features Carousel */}
            <div className="relative mt-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFeature}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="rounded-[32px] border border-white/20 bg-white/10 p-8 backdrop-blur-lg"
                >
                  <div className="mb-4 text-4xl">{features[activeFeature].icon}</div>
                  <h3 className="text-xl font-bold text-white">{features[activeFeature].title}</h3>
                  <p className="mt-2 text-sm text-white/70">{features[activeFeature].description}</p>
                </motion.div>
              </AnimatePresence>

              {/* Feature indicators */}
              <div className="mt-6 flex justify-center gap-2">
                {features.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveFeature(i)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === activeFeature ? 'w-8 bg-white' : 'w-2 bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8 flex items-center gap-4 text-sm text-white/60"
            >
              <span>✨ Modern & Intuitiv</span>
              <span>🔒 Enterprise Security</span>
              <span>⚡ Echtzeit-Synchronisierung</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Panel - Login Form */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center justify-center px-4 py-10 md:px-8"
        >
          <div className={`w-full max-w-xl rounded-[48px] border p-8 shadow-2xl md:p-10 ${currentTheme.card}`}>
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-8"
            >
              <div className="flex flex-col items-center text-center">
                {/* Logo with pulse effect */}
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-3xl border-2 border-white/20 bg-gradient-to-br from-blue-600 to-indigo-600 shadow-xl"
                >
                  {company.logoUrl ? (
                    <img
                      src={company.logoUrl}
                      alt="Logo"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-600 text-3xl font-black text-white">
                      {companyName.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  
                  {/* Status indicator */}
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-green-500" />
                </motion.div>

                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mt-6 text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500"
                >
                  {labels.privateApp}
                </motion.p>

                <motion.h2 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-white"
                >
                  {companyName}
                </motion.h2>

                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400"
                >
                  {labels.loginSubtitle}
                </motion.p>
              </div>
            </motion.div>

            <motion.form 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              onSubmit={handleSubmit} 
              className="space-y-5"
            >
              <Field
                label={labels.email}
                value={email}
                onChange={setEmail}
                placeholder={labels.emailPlaceholder}
                type="email"
                icon="📧"
              />

              <Field
                label={labels.password}
                value={password}
                onChange={setPassword}
                placeholder={labels.passwordPlaceholder}
                type={showPassword ? "text" : "password"}
                icon="🔒"
                showPasswordToggle
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
              />

              {/* Remember me checkbox - optional, you can remove this too if you want */}
              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    className="h-4 w-4 rounded border-slate-300 text-blue-600"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {isGerman ? "Angemeldet bleiben" : "Qëndro i kyçur"}
                  </span>
                </label>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="relative mt-4 inline-flex h-14 w-full items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-r from-[#0a1a2f] to-[#1e3a8a] px-4 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="h-5 w-5 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  labels.loginButton
                )}
              </motion.button>
            </motion.form>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-8 border-t border-slate-200 pt-8 dark:border-slate-800"
            >
              <p className="mb-4 text-center text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                {labels.languageLabel}
              </p>

              <div className="flex items-center justify-center gap-3">
                <LanguageButton
                  active={lang === "sq"}
                  onClick={() => setLang("sq")}
                  flag={alFlag}
                  label="Shqip"
                />

                <LanguageButton
                  active={lang === "de"}
                  onClick={() => setLang("de")}
                  flag={deFlag}
                  label="Deutsch"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Enhanced Field component with animations
function Field({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  type = "text", 
  icon,
  showPasswordToggle,
  showPassword,
  onTogglePassword 
}) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-2"
    >
      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
        {label}
      </label>
      
      <div className="relative">
        {icon && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">
            {icon}
          </span>
        )}
        
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`h-14 w-full rounded-2xl border bg-slate-50 px-12 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 ${
            isFocused 
              ? 'border-blue-500 ring-4 ring-blue-500/10' 
              : 'border-slate-200 dark:border-slate-700'
          }`}
          autoComplete={type === "password" ? "current-password" : "username"}
        />
        
        {showPasswordToggle && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            {showPassword ? "👁️" : "👁️‍🗨️"}
          </button>
        )}
      </div>
    </motion.div>
  );
}

// Language button component
function LanguageButton({ active, onClick, flag, label }) {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-3 rounded-2xl border px-5 py-3 text-sm font-semibold transition-all ${
        active
          ? "border-slate-900 bg-slate-900 text-white shadow-lg dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
          : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      }`}
    >
      <img src={flag} alt={label} className="h-4 w-6 rounded-sm object-cover shadow-sm" />
      <span>{label}</span>
    </motion.button>
  );
}